import { BoundCheckboxField, Css, EntityLockup, Tag } from "@homebound/beam";
import type { FieldState } from "@homebound/form-state";
import { ReadyPlanOptionGroupPersonalizationType as PersonalizationType } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { ConflictIcon } from "src/routes/projects/change-events/guided/workflows/components/ConflictIcon";
import {
  conflictMessage,
  cutoffTag,
  imageUrl,
  type OptionTags,
} from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import {
  OptionPrice,
  OptionPriceMode,
  optionAmountInCents,
} from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import type {
  ProductOptionCard_CheckoutConfigOptionGroupFragment,
  ProductOptionCard_CheckoutConfigPotentialOptionFragment,
} from "src/routes/projects/change-events/guided/workflows/components/ProductOptionCard.generated";
import { fail } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

export type ProductOptionCardProps = {
  group: ProductOptionCard_CheckoutConfigOptionGroupFragment;
  /** The option to show in the lockup. Defaults to the group's first option, which for an add-on is its only one. */
  option?: ProductOptionCard_CheckoutConfigPotentialOptionFragment;
  /** The form field that stores whether the group is included in the change. Without one there's no checkbox. */
  field?: FieldState<boolean | null | undefined>;
  /** Replaces the checkbox's default behavior of writing straight to `field`, for callers that need to do more on toggle. */
  onChange?: (checked: boolean) => void;
  /** The option's `childGroup` and the option picked in it, if any; they fill in the description, image and price. */
  child?: {
    group: ProductOptionCard_CheckoutConfigOptionGroupFragment;
    option: ProductOptionCard_CheckoutConfigPotentialOptionFragment | undefined;
  };
  priceMode?: OptionPriceMode;
  /** Tags keyed by `ReadyPlanOption` id; the shown option's (else the picked child option's) sits next to the brand. */
  optionTags?: OptionTags;
};

/**
 * Renders a product option, e.g. holiday lighting or a flooring plank. It shows a checkbox that includes the group in
 * the change, next to a lockup with the option's image, brand, name, location, description, cutoff and price.
 */
export function ProductOptionCard(props: ProductOptionCardProps) {
  const { group, field, onChange, child, priceMode = OptionPriceMode.Price, optionTags } = props;
  const potential =
    props.option ??
    group.options.first ??
    // A group always has at least one option, so an empty one is a data error and not something we should render around.
    fail(`Option group ${group.id} has no options`);
  const { option } = potential;
  const location = group.group.readyPlanLocation?.marketingName;
  const description =
    optionDescription(group, potential) ?? child?.group.group.marketingDescription ?? child?.option?.option.name;
  const tag = cutoffTag(group.cutoffDate);
  const optionTag = optionTags?.[option.id] ?? (child?.option && optionTags?.[child.option.option.id]);
  const brand = option.materialVariant?.listing.brand?.name;
  const imgSrc = imageUrl(potential) ?? (child?.option && imageUrl(child.option)) ?? PRODUCT_FALLBACK_IMG_URL;
  const priceInCents =
    optionAmountInCents(potential, priceMode) + (child?.option ? optionAmountInCents(child.option, priceMode) : 0);
  const conflict = conflictMessage(
    option.name,
    potential.wouldDeselect,
    potential.isSelected ? "Removing" : "Selecting",
  );

  return (
    <div css={Css.df.aifs.gap2.$}>
      {field && (
        <div css={Css.fs0.$}>
          <BoundCheckboxField
            field={field}
            label={option.name}
            checkboxOnly
            disabled={disableBasedOnPotentialOperation(potential.canBeSelected)}
            onChange={onChange}
          />
        </div>
      )}
      <div css={Css.fg1.mw0.$}>
        <EntityLockup
          imgSrc={imgSrc}
          // The lockup's title only takes text, so the option's tag rides along with the brand above it
          eyebrow={
            optionTag ? (
              <span css={Css.df.aic.gap1.$}>
                {brand}
                <Tag {...optionTag} />
              </span>
            ) : (
              brand
            )
          }
          title={option.name}
          description={
            <div css={Css.df.fdc.gapPx(2).$}>
              {location && <div>Location: {location}</div>}
              {description && <div css={Css.lineClamp2.$}>{description}</div>}
            </div>
          }
          right={
            <>
              <ConflictIcon message={conflict} />
              {tag && <Tag {...tag} />}
              <OptionPrice priceInCents={priceInCents} displayDirection={priceMode === OptionPriceMode.Price} />
            </>
          }
        />
      </div>
    </div>
  );
}

function optionDescription(
  group: ProductOptionCard_CheckoutConfigOptionGroupFragment,
  potential: ProductOptionCard_CheckoutConfigPotentialOptionFragment,
): string | null | undefined {
  const { marketingDescription, displayPackageOptionsAsSingleProduct, personalizationType } = group.group;
  const { option } = potential;
  if (displayPackageOptionsAsSingleProduct) return marketingDescription;
  switch (personalizationType?.code) {
    case PersonalizationType.SingleProduct:
      return option.description ?? option.materialVariant?.listing.description;
    case PersonalizationType.LinkedProduct:
      return marketingDescription ?? option.description ?? option.globalOption.marketingDescription;
    default:
      // Add-ons and structural options describe themselves through their global option
      return option.globalOption.marketingDescription;
  }
}
